import { fetchSocketToken, fetchSocketTokenFullfil } from 'actions';
import sendMessage, {
  initAgentSession,
  initAgentSessionFailure,
  initAgentSessionRequest,
  initAgentSessionSuccess,
  sendMessageAgentSessionRequest,
  setInitialResponse,
  updateAgentSession,
} from 'actions/agentSessions';
import { EventChannel, eventChannel } from 'redux-saga';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getUserCustomer } from 'selectors/auth';
import { ChatAgentSession, SagaReturn } from 'types';
import {
  AppendEventResponseData,
  DocumentsConnection,
  getWebsocketClient,
  joinAgentChannel,
  WrappedSocketResponse,
} from 'utils/socket';

function createSocketChannel(
  socket: DocumentsConnection
): EventChannel<WrappedSocketResponse<AppendEventResponseData>> {
  return eventChannel<WrappedSocketResponse<AppendEventResponseData>>((emit) => {
    const handleAgentSessionMessage = (data: AppendEventResponseData | null): void => {
      if (data) {
        emit({ type: 'session_update', payload: data });
      }
    };

    const handleGenericReply = (data: AppendEventResponseData | null): void => {
      if (data) {
        emit({ type: 'reply', payload: data });
      }
    };

    socket.registerOnMessageReceived('agent_session', 'agent', handleAgentSessionMessage);
    socket.registerOnMessageReceived('phx_reply', 'agent', handleGenericReply);

    const unsubscribe = (): void => {
      // moot
    };

    return unsubscribe;
  });
}

function* initAgentSessionSaga(action: ReturnType<typeof initAgentSession>): SagaReturn {
  const { agentSessionId, investigationId } = action.payload;

  const customer = (yield select(getUserCustomer)) as ReturnType<typeof getUserCustomer>;

  yield put(initAgentSessionRequest());

  yield put(fetchSocketToken());
  const { payload: token } = (yield take(fetchSocketTokenFullfil)) as ReturnType<
    typeof fetchSocketTokenFullfil
  >;

  if (!token) {
    yield put(initAgentSessionFailure({ message: 'Failed to fetch socket token', code: '400' }));
  } else {
    let socketChannel: EventChannel<AppendEventResponseData> | null = null;

    try {
      const socket = (yield call(
        joinAgentChannel,
        agentSessionId,
        token,
        customer?.uuid || ''
      )) as { response: ChatAgentSession; connection: DocumentsConnection };

      yield call(socket.connection.sendMessageAgentChannel, 'update', {
        id: agentSessionId,
        patch: { context: { investigation_uuid: investigationId } },
      });

      socketChannel = (yield call(
        createSocketChannel,
        socket.connection
      )) as EventChannel<AppendEventResponseData>;
      yield put(initAgentSessionSuccess({ sessionId: agentSessionId }));
      if (socket.response) {
        yield put(setInitialResponse(socket.response));
      }
    } catch (e) {
      const error = e as Error;
      yield put(initAgentSessionFailure({ message: error.message, code: '400' }));
    }

    if (socketChannel) {
      while (true) {
        try {
          const response = (yield take(
            socketChannel
          )) as WrappedSocketResponse<AppendEventResponseData>;

          if (response.type === 'session_update') {
            yield put(updateAgentSession(response.payload));
          }
        } catch (e) {
          const error = e as Error;
          yield put(initAgentSessionFailure({ message: error.message, code: '400' }));
        }
      }
    }
  }
}

function* sendMessageSaga(action: ReturnType<typeof sendMessage>): SagaReturn {
  const { message } = action.payload;

  const socket = getWebsocketClient();
  yield put(sendMessageAgentSessionRequest({ message }));

  if (socket) {
    yield call(socket.appendEvent, {
      content: message,
      metadata: {},
      role: 'user',
      type: 'message',
    });
  }
}

function* agentSessionsSaga(): SagaReturn {
  yield takeLatest(initAgentSession.toString(), initAgentSessionSaga);
  yield takeLatest(sendMessage.toString(), sendMessageSaga);
}

export default agentSessionsSaga;
